import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { path: '/', label: 'HOME' },
    { path: '/about', label: 'ABOUT' },
    { path: '/ministries', label: 'MINISTRIES' },
    { path: '/sermons', label: 'SERMONS' },
    { path: '/contact', label: 'CONTACT' },
    { path: '/events', label: 'EVENTS' },
    { path: '/give', label: 'GIVE' },
    { path: '/next-steps', label: 'NEXT STEPS' },
  ];

  return (
    <Nav $isScrolled={isScrolled}>
      <NavBackground $isScrolled={isScrolled} />
      <NavContainer>
        <LogoSection>
          <LogoLink to="/">
            <LogoImage 
              src="/images/logo-white.png" 
              alt="Embassy of Life Chapel" 
              $isScrolled={isScrolled}
            />
          </LogoLink>
          <LogoDivider $isScrolled={isScrolled}>
            <div className="line" />
            <div className="cross">✝</div>
            <div className="line" />
          </LogoDivider>
        </LogoSection>

        <MenuSection>
          <DesktopMenu>
            {menuItems.map((item) => (
              <MenuItem 
                key={item.path} 
                $isActive={location.pathname === item.path}
                $isScrolled={isScrolled}
              >
                <Link to={item.path}>{item.label}</Link>
              </MenuItem>
            ))}
          </DesktopMenu>

          <MenuButton onClick={() => setIsOpen(!isOpen)} $isScrolled={isScrolled}>
            <span></span>
            <span></span>
            <span></span>
          </MenuButton>
        </MenuSection>

        <AnimatePresence>
          {isOpen && (
            <MobileMenu
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {menuItems.map((item) => (
                <MobileMenuItem 
                  key={item.path}
                  onClick={() => setIsOpen(false)}
                >
                  <Link to={item.path}>{item.label}</Link>
                </MobileMenuItem>
              ))}
            </MobileMenu>
          )}
        </AnimatePresence>
      </NavContainer>
    </Nav>
  );
};

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.4s ease;
  height: ${props => props.$isScrolled ? '90px' : '120px'};
  display: flex;
  align-items: center;
  padding-bottom: ${props => props.$isScrolled ? '1rem' : '1.5rem'};
`;

const NavBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.$isScrolled 
    ? 'rgba(64, 0, 112, 0.98)'
    : 'linear-gradient(180deg, rgba(64, 0, 112, 1) 0%, rgba(64, 0, 112, 0.95) 100%)'};
  backdrop-filter: ${props => props.$isScrolled ? 'blur(10px)' : 'blur(5px)'};
  transition: all 0.4s ease;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent,
      rgba(255, 255, 255, 0.3) 20%,
      rgba(255, 255, 255, 0.5) 50%,
      rgba(255, 255, 255, 0.3) 80%,
      transparent
    );
  }
`;

const NavContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 3rem;
  position: relative;
  z-index: 2;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  padding: 0.5rem 0;
  background: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 70%
  );
`;

const LogoLink = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const LogoImage = styled.img`
  height: ${props => props.$isScrolled ? '80px' : '100px'};
  width: auto;
  transition: all 0.4s ease;
  filter: drop-shadow(0 0 20px rgba(255,255,255,0.2)) brightness(1.1) contrast(1.1);

  &:hover {
    filter: drop-shadow(0 0 25px rgba(255,255,255,0.3)) brightness(1.2) contrast(1.1);
    transform: scale(1.02);
  }
`;

const LogoDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: 2rem;
  opacity: ${props => props.$isScrolled ? '1' : '0.9'};
  transition: all 0.4s ease;

  .line {
    height: 50px;
    width: 1px;
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(255, 255, 255, 0.4) 30%,
      rgba(255, 255, 255, 0.4) 70%,
      transparent
    );
  }

  .cross {
    font-size: 1.2rem;
    color: #FFD700;
    margin: 0 -0.5rem;
    transform: translateY(-2px);
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.3);
  }
`;

const MenuSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const DesktopMenu = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  justify-content: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MenuItem = styled.div`
  position: relative;
  padding: 0.75rem 0;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0.5rem;
    left: 50%;
    width: ${props => props.$isActive ? '100%' : '0'};
    height: 2px;
    background: ${props => props.$isActive ? '#FFD700' : 'rgba(255,255,255,0.8)'};
    transition: all 0.3s ease;
    transform: translateX(-50%);
    box-shadow: ${props => props.$isActive ? '0 0 10px rgba(255, 215, 0, 0.5)' : 'none'};
  }

  &:hover::after {
    width: 100%;
    background: #FFD700;
    box-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
  }

  a {
    text-decoration: none;
    color: ${props => props.$isActive ? '#FFD700' : `rgba(255,255,255,${props.$isScrolled ? '0.9' : '1'})`};
    font-weight: ${props => props.$isActive ? '600' : '500'};
    font-size: 0.85rem;
    letter-spacing: 2px;
    padding: 0.5rem 0;
    transition: all 0.3s ease;
    font-family: 'Montserrat', sans-serif;
    text-shadow: ${props => props.$isActive ? '0 0 10px rgba(255, 215, 0, 0.3)' : 'none'};
    
    &:hover {
      color: #FFD700;
      transform: translateY(-1px);
    }
  }

  &:hover a {
    color: #FFD700;
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.3);
  }
`;

const MenuButton = styled.button`
  display: none;
  flex-direction: column;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 1rem;
  margin-left: auto;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
  }

  span {
    width: 24px;
    height: 2px;
    background-color: #fff;
    transition: all 0.3s ease;
    opacity: ${props => props.$isScrolled ? '0.9' : '1'};
  }

  &:hover span {
    opacity: 1;
  }
`;

const MobileMenu = styled(motion.div)`
  display: none;
  position: fixed;
  top: ${props => props.$isScrolled ? '90px' : '120px'};
  right: 0;
  bottom: 0;
  width: 100%;
  background: linear-gradient(160deg, rgba(64, 0, 128, 0.98) 0%, rgba(128, 0, 128, 0.95) 100%);
  padding: 2rem;
  backdrop-filter: blur(10px);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
`;

const MobileMenuItem = styled.div`
  a {
    text-decoration: none;
    color: ${props => props.$isActive ? '#FFD700' : 'rgba(255,255,255,0.9)'};
    font-size: 1rem;
    padding: 1rem 0;
    display: block;
    transition: all 0.3s ease;
    letter-spacing: 2px;
    font-family: 'Montserrat', sans-serif;
    font-weight: ${props => props.$isActive ? '600' : '500'};
    text-shadow: ${props => props.$isActive ? '0 0 10px rgba(255, 215, 0, 0.3)' : 'none'};

    &:hover {
      color: #FFD700;
      padding-left: 0.5rem;
      text-shadow: 0 0 10px rgba(255, 215, 0, 0.3);
    }
  }
`;

export default Navigation; 